// Pivot + Grid visual — both rendered with ProPivot (@proteus/propivot), the
// open-source pivot engine that replaces WebDataRocks. ProPivot is a drop-in: it
// consumes the identical report shape, so the Rows / Columns / Values column
// groups are translated into a "slice" (rows[], columns[]+[Measures], measures[]
// with aggregation), exactly as insight-bi built the Flexmonster report.
//
// A single component drives TWO visual types via `variant`:
//   • 'pivot' (default) — the cross-tab: Rows / Columns / Values groups.
//   • 'grid'            — a plain table: EVERY visible column placed in
//                         `slice.rows` with `grid.type = 'flat'` and no measures,
//                         so ProPivot renders a flat, sortable table (replacing
//                         the old ag-grid path).
//
// ProPivot is an EXTERNAL library that lives in its own sibling project
// (../ProPivot) and is lazy-loaded on first render; until it is ready (or if it
// fails to load) a dependency-free table (the `renderFallback` for grid, the
// cross-tab PivotTable for pivot) renders the same data so it is never blank.

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { ensureProPivot } from './loader'
import { humanizeColumnLabel, mapVisualData, pickGroup, toMappedColumn, type MappedColumn, type MappedData } from './dataUtils'
import { parseAdvFormat } from './conditionalFormat'
import { maskToDisplay } from './formatters'
import { readStrOption, readBoolOption } from './optionUtils'
import type { NativeRendererProps } from './nativeRenderers'
import { PivotTable } from './nativeRenderers'
import type { VisualJsonData, VisualLayout, VisualOption } from './types'

// insight-bi FUNCTION → pivot aggregation name (shared by WebDataRocks /
// ProPivot — same aggregation vocabulary). PIVOT_AGG (c.pivotAgg) overrides this.
const WDR_AGG: Record<string, string> = {
  SUM: 'sum', AVG: 'average', AVERAGE: 'average', MIN: 'min', MAX: 'max',
  COUNT: 'count', 'DISTINCT COUNT': 'distinctcount', DISTINCTCOUNT: 'distinctcount',
}

// Positional aggregation family — these honour PIVOT_AXIS (positionalAxis).
const POSITIONAL = new Set(['difference', '%difference', 'runningtotals'])

let _pivotSeq = 0

type DisplayObj = Record<string, unknown>

function mappingType(c: MappedColumn): string {
  if (c.coltype === 'NUMBER' || c.type === 'number') return 'number'
  if (c.coltype === 'DATE' || c.type === 'datetime') return 'date string'
  return 'string'
}

/** The rich display for a pivot dimension/measure: authored `display` wins, else
 *  synthesize one from the legacy FORMAT / PATTERN masks. */
function displayFor(c: MappedColumn): DisplayObj | undefined {
  return c.display ?? maskToDisplay(c.format, c.pattern, c.coltype)
}

const isDateColumn = (c: MappedColumn) =>
  c.coltype === 'DATE' || c.type === 'datetime' || c.type === 'date'

// How ProPivot writes a date MEMBER's label (options.datePattern). A member's
// `display` is then applied to that label, not to the original value — so the
// label must be one `new Date()` reads back unambiguously. ProPivot's own
// default, dd/MM/yyyy, is read as MM/dd: 5 Oct became "10-May-26", and any day
// past the 12th failed to parse and showed raw. ISO local time (no zone) also
// keeps the calendar day in every timezone and sorts chronologically.
const MEMBER_DATE_PATTERN = 'yyyy-MM-ddTHH:mm:ss'

/** A pivot dimension's display: as `displayFor`, but a date dimension with none
 *  gets the grid's dd-MMM-yy default, since its raw label is the ISO form above. */
function dimDisplayFor(c: MappedColumn): DisplayObj | undefined {
  const disp = displayFor(c)
  if (disp || !isDateColumn(c)) return disp
  return { type: 'date', datePattern: c.pattern && c.pattern.trim() ? c.pattern : 'dd-MMM-yy' }
}

/** Grid member-cell display: authored `display` → date default → legacy mask →
 *  an ADV_FORMAT-derived background (ProPivot `conditions` target measures only,
 *  which a flat grid has none of, so member coloring rides on `display`). */
function gridDisplayFor(c: MappedColumn): DisplayObj | undefined {
  if (c.display) return c.display
  if (isDateColumn(c)) {
    const dp = c.pattern && c.pattern.trim() ? c.pattern : 'dd-MMM-yy'
    return { type: 'date', datePattern: dp }
  }
  const mask = maskToDisplay(c.format, c.pattern, c.coltype)
  if (mask) return mask
  return advToBackgroundDisplay(c.advFormat)
}

/** Convert an ADV_FORMAT string into a ProPivot `background` display so a flat
 *  grid's member cells still colour by rule (background only — exact text
 *  colour / weight degrade; documented in visuals.md). */
function advToBackgroundDisplay(advFormat?: string): DisplayObj | undefined {
  const conds = parseAdvFormat(advFormat)
  if (!conds.length) return undefined
  const rules = conds
    .map((cond) => {
      const color = cond.format.backgroundColor || cond.format.color
      if (!color) return null
      return { when: cond.formula.replace(/#value#/gi, 'value'), color }
    })
    .filter((r): r is { when: string; color: string } => !!r)
  if (!rules.length) return undefined
  return { type: 'background', rules }
}

/** All visible columns in ColumnGroup order (deduped by NAME), for the grid. */
function resolveGridColumns(jsonData: VisualJsonData, visualLayout: VisualLayout): MappedColumn[] {
  const schema = (jsonData?.[0] ?? {}) as Record<string, { type?: string }>
  const cols: MappedColumn[] = []
  const seen = new Set<string>()
  for (const group of visualLayout?.ColumnGroups ?? []) {
    const columns = (group as { COLUMNS?: Array<Record<string, unknown>> }).COLUMNS ?? []
    for (const c of columns) {
      const name = (c.NAME ?? c.name) as string | undefined
      if (!name || seen.has(name)) continue
      seen.add(name)
      const mc = toMappedColumn(c, name, schema)
      if (!mc.hidden) cols.push(mc)
    }
  }
  if (cols.length === 0) {
    for (const k of Object.keys(schema)) {
      if (k === 'id') continue
      cols.push({ name: k, label: humanizeColumnLabel(k), type: schema[k]?.type })
    }
  }
  return cols
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ProPivotMeasure = Record<string, any>
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ProPivotHierarchy = Record<string, any>

/** Build the ProPivot report + the schema row (for selection coercion). */
function buildReport(
  jsonData: VisualJsonData,
  visualLayout: VisualLayout,
  options: VisualOption[] | undefined,
  variant: 'pivot' | 'grid',
) {
  const md: MappedData = mapVisualData(jsonData, visualLayout)
  const schemaRow = (jsonData?.[0] ?? {}) as Record<string, { type?: string }>

  const enrichDim = (c: MappedColumn): ProPivotHierarchy => {
    const disp = dimDisplayFor(c)
    return {
      uniqueName: c.name,
      caption: c.label,
      ...(disp ? { display: disp } : {}),
      ...(c.width ? { width: c.width } : {}),
    }
  }

  const mapping: Record<string, { type: string; caption: string }> = {}

  let slice: { rows: ProPivotHierarchy[]; columns: ProPivotHierarchy[]; measures: ProPivotMeasure[] }
  let gridDefaults: { type: string; showTotals: string; showGrandTotals: string }
  const conditions: Array<{ formula: string; measure: string; format: Record<string, string> }> = []

  if (variant === 'grid') {
    const cols = resolveGridColumns(jsonData, visualLayout)
    for (const c of cols) mapping[c.name] = { type: mappingType(c), caption: c.label }
    const rows = cols.map((c): ProPivotHierarchy => {
      const disp = gridDisplayFor(c)
      return {
        uniqueName: c.name,
        caption: c.label,
        ...(disp ? { display: disp } : {}),
        ...(c.width ? { width: c.width } : {}),
      }
    })
    slice = { rows, columns: [], measures: [] }
    gridDefaults = { type: 'flat', showTotals: 'off', showGrandTotals: 'off' }
  } else {
    const rowsG = pickGroup(md, 'rows').filter((c) => !c.hidden)
    const colsG = pickGroup(md, 'columns').filter((c) => !c.hidden)
    const valsG = pickGroup(md, 'values', 'measures').filter((c) => !c.hidden)

    for (const c of [...rowsG, ...colsG, ...valsG]) mapping[c.name] = { type: mappingType(c), caption: c.label }

    const columns = colsG.map(enrichDim)
    if (valsG.length) columns.push({ uniqueName: '[Measures]', caption: 'Measures' })

    // Calculated (Presentation) measures become formula measures; the rest are
    // aggregated. PIVOT_AGG (c.pivotAgg) wins over the legacy FUNCTION mapping.
    const measures = valsG.map((c): ProPivotMeasure => {
      const disp = displayFor(c)
      const common: ProPivotMeasure = {
        uniqueName: c.name,
        caption: c.label,
        ...(disp ? { display: disp } : {}),
        ...(c.width ? { width: c.width } : {}),
      }
      if (c.isCalc && c.calcExpression) {
        return { ...common, formula: c.calcExpression }
      }
      const agg = c.pivotAgg
        ? String(c.pivotAgg).toLowerCase()
        : (c.agg && WDR_AGG[c.agg.toUpperCase()]) || 'sum'
      const m: ProPivotMeasure = { ...common, aggregation: agg }
      if (POSITIONAL.has(agg) && (c.pivotAxis === 'rows' || c.pivotAxis === 'columns')) {
        m.positionalAxis = c.pivotAxis
      }
      return m
    })

    // ADV_FORMAT → ProPivot conditions on value cells: {formula, measure, format}.
    for (const c of [...rowsG, ...colsG, ...valsG]) {
      for (const cond of parseAdvFormat(c.advFormat)) {
        conditions.push({ formula: cond.formula, measure: c.name, format: cond.format })
      }
    }

    slice = { rows: rowsG.map(enrichDim), columns, measures }
    gridDefaults = { type: 'compact', showTotals: 'on', showGrandTotals: 'on' }
  }

  const gridType = readStrOption(options, 'type', gridDefaults.type)
  const showTotals = readStrOption(options, 'showTotals', gridDefaults.showTotals)
  const showGrandTotals = readStrOption(options, 'showGrandTotals', gridDefaults.showGrandTotals)
  const showAggregationLabels = readBoolOption(options, 'showAggregationLabels', true)

  const report = {
    dataSource: { type: 'json', data: md.rows, mapping },
    slice,
    options: {
      grid: { type: gridType, showTotals, showGrandTotals, showAggregationLabels },
      // Suppress ProPivot's OWN ⋮ / field-list configurator button. Native
      // visuals dock the single platform-wide <NativeVisualMenu> into the host
      // header; ProPivot's own fieldList menu would paint a SECOND floating ⋮.
      configuratorButton: false,
      datePattern: MEMBER_DATE_PATTERN,
    },
    ...(conditions.length ? { conditions } : {}),
  }
  return { report, schemaRow }
}

/** Map a clicked cell's row tuple → { [uniqueName]: member }, coercing NUMBER
 *  columns via the jsonData schema row (drives drill-through / page vars). */
function rowFromTuple(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  tuple: Array<{ uniqueName?: string; member?: string }> | undefined,
  schemaRow: Record<string, { type?: string }>,
): Record<string, unknown> {
  const row: Record<string, unknown> = {}
  for (const t of tuple ?? []) {
    if (!t || !t.uniqueName || t.member === undefined) continue
    let v: unknown = t.member
    if (schemaRow?.[t.uniqueName]?.type === 'number') {
      const n = Number(v)
      if (Number.isFinite(n)) v = n
    }
    row[t.uniqueName] = v
  }
  return row
}

export interface PivotVisualProps extends NativeRendererProps {
  /** 'pivot' (cross-tab, default) or 'grid' (flat table — all columns in rows). */
  variant?: 'pivot' | 'grid'
  /** Loading / load-failure renderer. Defaults to the dependency-free cross-tab. */
  renderFallback?: () => ReactNode
}

// Height given to the pivot in an auto-height host (see `autoHeight` below).
// ProPivot scrolls its body internally, so this is a viewport, not a cap on the
// data: the same number the pixel fallback has always used.
const AUTO_HEIGHT_PX = 400

export function PivotVisual({
  jsonData, visualLayout, optionsArr, onSelect, variant = 'pivot', renderFallback,
  autoHeight = false, dark = false,
}: PivotVisualProps) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const ref = useRef<HTMLDivElement>(null)
  const idRef = useRef<string>(`propivot-${(_pivotSeq += 1)}`)
  const [ready, setReady] = useState(false)
  const [failed, setFailed] = useState(false)

  // InsightVisual defaults optionsArr to visualLayout.options; keep the local
  // fallback too for direct consumers (CardsVisual:465 pattern).
  const options = (optionsArr ?? visualLayout?.options) as VisualOption[] | undefined

  const { report: built, schemaRow } = useMemo(
    () => buildReport(jsonData, visualLayout, options, variant),
    [jsonData, visualLayout, options, variant],
  )
  // ProPivot's own light / dark theme, chosen by the surface behind the visual
  // (InsightVisual → surfaceTheme.ts). Without it the grid kept its white
  // background while the host's dark text colour reached its cells.
  const report = useMemo(
    () => ({ ...built, options: { ...(built.options || {}), theme: dark ? 'dark' : 'light' } }),
    [built, dark],
  )

  // Keep the latest onSelect + schema in a ref so the constructor's cellclick
  // closure always calls the current handler without rebuilding the pivot.
  const selectRef = useRef(onSelect)
  selectRef.current = onSelect
  const schemaRef = useRef(schemaRow)
  schemaRef.current = schemaRow

  useEffect(() => {
    let cancelled = false
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let pivot: any = null
    setReady(false)
    setFailed(false)
    ensureProPivot()
      .then((ProPivot) => {
        if (cancelled || !ref.current || typeof ProPivot !== 'function') {
          if (typeof ProPivot !== 'function') setFailed(true)
          return
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const Ctor = ProPivot as any
        // Size the pivot FLUIDLY ('100%' of our wrapper) rather than pinning the
        // pixel height measured at construction: the pane it lives in can grow
        // AFTER the pivot is built (e.g. the preview's criteria bar collapses on
        // Run, the tile is resized), and a pinned height would leave a dead strip
        // below the grid. Only a host that gives us a DEFINITE height can be
        // sized that way: in an auto-height host '100%' resolves to auto,
        // ProPivot measures a zero viewport and paints no rows at all — a grid
        // that is simply blank. The host tells us which it is; measuring the
        // wrapper cannot, because its own `minHeight` makes an auto-height box
        // report a non-zero clientHeight. (The measurement stays as the answer
        // for a direct consumer that passes no sizing mode.)
        const height = autoHeight
          ? AUTO_HEIGHT_PX
          : wrapRef.current?.clientHeight ? '100%' : AUTO_HEIGHT_PX
        pivot = new Ctor({
          container: `#${idRef.current}`,
          toolbar: false,
          height,
          width: '100%',
          report,
          reportcomplete: () => { if (!cancelled) setReady(true) },
          // Click on a non-total value / member cell with a full member tuple →
          // emit the drill row. Works for pivot value cells AND grid member cells.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          cellclick: (data: any) => {
            const cb = selectRef.current
            if (!cb || !data) return
            if (data.isTotal || data.isGrandTotal || data.isGrandTotalRow) return
            const tuple = data.rows as Array<{ uniqueName?: string; member?: string }> | undefined
            if (!tuple || !tuple.length || !tuple.every((t) => t && t.member !== undefined)) return
            cb(rowFromTuple(tuple, schemaRef.current))
          },
        })
      })
      .catch(() => { if (!cancelled) setFailed(true) })

    return () => {
      cancelled = true
      try {
        pivot?.dispose?.()
      } catch {
        /* ignore teardown errors */
      }
    }
  }, [report, autoHeight])

  // ProPivot virtualizes the body: it paints only the rows that fit the viewport
  // it measured at its last paint, and repaints on its scroll container's
  // `scroll` event. So when our pane GROWS after that paint (the preview's
  // criteria bar collapses on Run, a tile is resized, full-screen is toggled)
  // the extra height stays blank until the user scrolls. Watch our own box and
  // nudge the pivot with a synthetic scroll event so it repaints to fill it.
  useEffect(() => {
    const el = wrapRef.current
    if (!ready || !el || typeof ResizeObserver === 'undefined') return
    let raf = 0
    let last = el.clientHeight
    const ro = new ResizeObserver(() => {
      const h = el.clientHeight
      if (h === last) return
      last = h
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        try {
          ref.current?.querySelector('.pp-scroll')?.dispatchEvent(new Event('scroll'))
        } catch {
          /* repaint is best-effort — never break the host on a resize */
        }
      })
    })
    ro.observe(el)
    return () => { cancelAnimationFrame(raf); ro.disconnect() }
  }, [ready])

  const fallback = renderFallback
    ? renderFallback()
    : <PivotTable jsonData={jsonData} visualLayout={visualLayout} />

  return (
    // `isolation: isolate` gives the pivot its own stacking context so ProPivot's
    // sticky headers can never paint OVER neighboring tiles or the shared criteria bar.
    <div
      ref={wrapRef}
      style={{
        position: 'relative', width: '100%', isolation: 'isolate',
        // In an auto-height host the box IS the viewport we sized ProPivot to;
        // `height: 100%` there would resolve to auto and leave the engine
        // drawing into nothing.
        ...(autoHeight ? { height: AUTO_HEIGHT_PX } : { height: '100%', minHeight: 240 }),
      }}
    >
      <div id={idRef.current} ref={ref} style={{ width: '100%', height: '100%', display: ready ? 'block' : 'none' }} />
      {!ready && (
        <div style={{ position: 'absolute', inset: 0, overflow: 'auto' }}>
          {fallback}
          {!failed && (
            <div style={{ position: 'absolute', top: 6, right: 10, fontSize: 11, color: 'var(--pg-muted, var(--color-text-secondary, #9ca3af))' }}>
              loading {variant === 'grid' ? 'grid' : 'pivot'}…
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default PivotVisual
